/**
 * 评测记录（`#eval-records`）——PR6 升级（判定卡 06，升级（小），无线框）。
 *
 * 刀 0 原样拷自 Loop 评测记录页（来源见 COPIED_FROM.json）；本 PR 改动：
 * - 数据接 A-52 `GET /eval/batches`（批 2 时恒空，批次随批 3 评测装置写入）；
 * - 列加 状态（运行中 x/y ／ 中断带原因）、计划来源、配置快照号（点开配置版本）、题库快照号（同形态、只显示）；
 * - D-28 删 6-1「指南」列、6-2「CX」列、6-3 卡内说明段；卡头 ／ 筛选行的「共 x 批」→ 只在底部「共 N 条」；
 * - 手写表格 → PlanTable；自写翻页 → ui/Pagination；手搭 Radix 抽屉 → ui/Drawer；裸 number input → ui/Input；
 * - 三态：列表加载 ／ 失败 ／ 空；批次报告抽屉加载 ／ 失败，运行中的批次只显示进度不出报告；
 * - 批次报告抽屉加「写对象错误」「没办成说办成」两项（T-39），头部带两个快照号。
 * PR9：批次报告抽屉改用模拟场景的报告体（A-53 固定区块，eval/batch-report.tsx）；点一次试跑开运行轨迹抽屉 ＋ 评分卡条
 * （删掉拷来的手搭评分卡抽屉）；「次数」列改读每题次数（`trials_per_scenario`，原先误读成总试跑数），运行中 x/y 的分母改为总试跑数。
 */
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Drawer } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import {
	type PlanBatchRow,
	type PlanTrialResult,
	planErrorMessage,
	usePlanBatchReport,
	usePlanBatches,
} from "@/lib/api/plan-lab";

import { BatchReportBody, TrialDrawer } from "../eval/batch-report";
import { FailState } from "../kit/fail-state";
import { FilterSel, uniqStr } from "../kit/filter-sel";
import { MetricTip } from "../kit/metric-tip";
import { PageJump } from "../kit/page-jump";
import { BATCH_STATUS_TONE, BATCH_STATUS_ZH, BATCH_STOP_ZH, zh } from "../kit/plan-labels";
import { type PlanColumn, PlanTable } from "../kit/plan-table";
import { BankTag, SnapshotTag } from "../kit/snapshot-tag";

export const RECORDS_PAGE = 20;

const fmtAt = (at: string | null | undefined) => (at ? at.slice(5, 16).replace("T", " ") : "—");
const pct = (v: number | null | undefined) => (v == null ? "—" : `${Math.round(v * 100)}%`);
const dash = (v: unknown) => (v == null || v === "" ? "—" : String(v));
/** 本批总试跑数（A-52 `trial_count` 就是总数） */
const total = (r: PlanBatchRow) => r.trial_count ?? null;
const perScenario = (r: PlanBatchRow) => r.trials_per_scenario ?? null;

export function BatchStatus({ r }: { r: PlanBatchRow }) {
	const label = zh(BATCH_STATUS_ZH, r.status);
	const tone = BATCH_STATUS_TONE[r.status] ?? "muted";
	const t = total(r);
	const text =
		r.status === "running" && t != null
			? `${label} ${r.done ?? 0}/${t}`
			: (r.status === "interrupted" || r.status === "stopped") && r.stop_reason
				? `${label}（${zh(BATCH_STOP_ZH, r.stop_reason)}）`
				: label;
	return <Badge tone={tone}>{text}</Badge>;
}

function passK(r: PlanBatchRow): string {
	const e = Object.entries(r.pass_k ?? {});
	return e.length ? e.map(([k, v]) => `k${k}:${v}`).join(" ") : "—";
}

const COLUMNS: PlanColumn<PlanBatchRow>[] = [
	{ key: "at", title: "时间", render: (r) => fmtAt(r.created_at) },
	{ key: "status", title: "状态", render: (r) => <BatchStatus r={r} /> },
	{
		key: "n",
		title: "题数",
		align: "right",
		render: (r) => (r.scenario_count == null ? "—" : `${r.scenario_count} 题`),
	},
	{ key: "trials", title: "次数", align: "right", render: (r) => dash(perScenario(r)) },
	{
		key: "avg",
		title: <MetricTip id="eval.avg_reward">通过率</MetricTip>,
		align: "right",
		render: (r) => <span className="font-medium text-text-primary">{pct(r.avg_reward)}</span>,
	},
	{
		key: "action",
		title: <MetricTip id="eval.action">action</MetricTip>,
		align: "right",
		render: (r) => pct(r.dim_pass?.action),
	},
	{
		key: "communicate",
		title: <MetricTip id="eval.communicate">communicate</MetricTip>,
		align: "right",
		render: (r) => pct(r.dim_pass?.communicate),
	},
	{
		key: "nl",
		title: <MetricTip id="eval.nl">nl</MetricTip>,
		align: "right",
		render: (r) => pct(r.dim_pass?.nl),
	},
	{
		key: "passk",
		title: <MetricTip id="eval.pass_k">pass^k</MetricTip>,
		className: "max-w-[160px] truncate",
		render: (r) => <span className="text-xs text-text-muted">{passK(r)}</span>,
	},
	{ key: "snap", title: "配置快照", render: (r) => <SnapshotTag id={r.locked_snapshot_id} /> },
	{ key: "bank", title: "题库快照", render: (r) => <BankTag id={r.bank_id} /> },
];

/** 批次报告抽屉：复用模拟场景的报告体；点题再开评分卡 EvalDrawer（和模拟场景一致） */
export function BatchReportDrawer({
	row,
	onClose,
	onDrill,
}: {
	row: PlanBatchRow | null;
	onClose: () => void;
	onDrill: (r: PlanTrialResult, scenarioId: string) => void;
}) {
	const running = row?.status === "running" || row?.status === "queued";
	const report = usePlanBatchReport(row && !running ? row.batch_id : null);
	const t = row ? total(row) : null;
	return (
		<Drawer
			open={!!row}
			onClose={onClose}
			testId="plan-batch-drawer"
			title="批次报告"
			badges={
				row ? (
					<>
						<Badge tone="info">{fmtAt(row.created_at)}</Badge>
						<BatchStatus r={row} />
						{row.scenario_count != null ? (
							<Badge tone="muted">
								{row.scenario_count} 题 × {dash(perScenario(row))}
							</Badge>
						) : null}
						<span className="inline-flex items-center gap-1 text-xs text-text-muted">
							配置 <SnapshotTag id={row.locked_snapshot_id} />
						</span>
						<span className="inline-flex items-center gap-1 text-xs text-text-muted">
							题库 <BankTag id={row.bank_id} />
						</span>
					</>
				) : null
			}
		>
			{row ? (
				<div className="space-y-3">
					<div className="flex flex-wrap gap-2 text-xs" data-testid="plan-batch-counts">
						<Badge tone={row.write_object_errors ? "danger" : "muted"}>
							写对象错误 {dash(row.write_object_errors)}
						</Badge>
						<Badge tone={row.false_claims ? "danger" : "muted"}>
							没办成说办成 {dash(row.false_claims)}
						</Badge>
						<Badge tone={row.undetermined ? "warning" : "muted"}>
							未判定 {dash(row.undetermined)}
						</Badge>
					</div>
					{running ? (
						<div className="text-sm text-text-secondary">
							{zh(BATCH_STATUS_ZH, row.status)}
							{t != null ? ` · 已跑 ${row.done ?? 0}/${t}` : ""}；跑完后出报告。
						</div>
					) : report.isLoading ? (
						<LoadingInline text="加载批次报告…" />
					) : report.isError || !report.data ? (
						<FailState
							title="批次报告没加载出来"
							reason={report.error ? planErrorMessage(report.error) : "接口没有返回"}
							onRetry={() => report.refetch()}
							retrying={report.isFetching}
						/>
					) : (
						<BatchReportBody report={report.data} onDrill={onDrill} />
					)}
				</div>
			) : null}
		</Drawer>
	);
}

export function EvalRecordsTab() {
	const batches = usePlanBatches();
	const all = batches.data?.rows ?? [];
	const [fTrials, setFTrials] = useState("");
	const [fMinReward, setFMinReward] = useState("");
	const [sel, setSel] = useState<string | null>(null);
	const [drill, setDrill] = useState<{ id: string; label: string } | null>(null);
	const [page, setPage] = useState(0);
	const shown = all.filter(
		(r) =>
			(!fTrials || String(perScenario(r) ?? "") === fTrials) &&
			(!fMinReward || (r.avg_reward ?? 0) >= Number(fMinReward)),
	);
	const selRow = all.find((r) => r.batch_id === sel) ?? null;
	const pageCount = Math.max(1, Math.ceil(shown.length / RECORDS_PAGE));
	const p = Math.min(page, pageCount - 1);
	const pageItems = shown.slice(p * RECORDS_PAGE, p * RECORDS_PAGE + RECORDS_PAGE);

	return (
		<>
			<Card data-testid="plan-block-filters">
				<CardContent className="flex flex-wrap items-center gap-3 px-4 py-3 text-xs">
					<FilterSel
						label="次数"
						value={fTrials}
						onChange={(v) => {
							setFTrials(v);
							setPage(0);
						}}
						opts={uniqStr(all.map((r) => String(perScenario(r) ?? "")))}
					/>
					<span className="flex items-center gap-1">
						通过率≥
						<Input
							type="number"
							min={0}
							max={1}
							step={0.1}
							value={fMinReward}
							onChange={(e) => {
								setFMinReward(e.target.value);
								setPage(0);
							}}
							className="w-20"
							aria-label="通过率下限（0～1）"
						/>
					</span>
				</CardContent>
			</Card>
			<Card data-testid="plan-block-batches">
				<CardContent className="space-y-3 p-4">
					{batches.isLoading ? (
						<LoadingInline text="加载评测批次…" />
					) : batches.isError ? (
						<FailState
							title="没取到评测批次"
							reason={planErrorMessage(batches.error)}
							onRetry={() => batches.refetch()}
							retrying={batches.isFetching}
							testId="plan-batches-fail"
						/>
					) : (
						<>
							<PlanTable
								rows={pageItems}
								rowKey={(r) => r.batch_id}
								rowTestId="plan-batch-row"
								onRowClick={(r) => setSel(r.batch_id)}
								columns={COLUMNS}
							/>
							{!shown.length ? (
								<div data-testid="plan-batches-empty">
									{all.length ? (
										<EmptyState title="当前筛选下没有批次" desc="放宽筛选条件再看。" />
									) : (
										<EmptyState
											title="还没有评测批次"
											desc="在「模拟场景」发起评测后，每一批都列在这里，带按哪份配置、哪份题库跑的。"
										/>
									)}
								</div>
							) : null}
							<div className="flex items-center justify-between gap-3 text-xs text-text-muted">
								<span data-testid="plan-batches-total">共 {shown.length} 条</span>
								<span className="mr-auto">
									批次在 <PageJump to="eval" /> 发起，过败口径见 <PageJump to="eval-rules" />
								</span>
								<Pagination page={p} pageCount={pageCount} onChange={setPage} />
							</div>
						</>
					)}
				</CardContent>
			</Card>
			<BatchReportDrawer
				row={selRow}
				onClose={() => setSel(null)}
				onDrill={(r, sid) =>
					setDrill({ id: r.trial_id, label: `${sid} · 第 ${(r.index ?? 0) + 1} 次` })
				}
			/>
			<TrialDrawer
				trialId={drill?.id ?? null}
				label={drill?.label}
				onClose={() => setDrill(null)}
			/>
		</>
	);
}
